import * as t from 'io-ts';
import {isObject, values} from 'lodash';

import {logger} from '../../../srcCommon/services/logger/logger';
import {
  parseActivitySource,
  parseMessageActivitySource,
  RActivitySource,
  RMessageActivitySource,
} from './activitySourceModel';
import {
  makeApiType,
  makeEnumRuntimeType,
  makeIdRefType,
  RCommentUidRef,
  RInboxIdRef,
  RLazyTeammateIdRef,
  RMessageUidRef,
  RTagIdRef,
  RTopicIdRef,
} from './apiTypes';

const activityModelLogger = logger.getLogger('activity-model');

/*
 * Enum mania.
 */

// Comment.
export enum CommentActivityTypesEnum {
  COMMENT = 'comment',
  MENTION = 'mention',
}

// Message.
export enum MessageActivityTypesEnum {
  OUTBOUND = 'outbound',
  OUT_REPLY = 'out_reply',
  INBOUND = 'inbound',
}

// Basic.
export enum BasicActivityTypesEnum {
  ASSIGN = 'assign',
  UNASSIGN = 'unassign',
  ARCHIVE = 'archive',
  REOPEN = 'reopen',
  TOPIC_ADDED = 'topic_added',
  TOPIC_CLOSED = 'topic_closed',
  TOPIC_REMOVED = 'topic_removed',
  TRASH = 'trash',
  RESTORE = 'restore',
  TAG = 'tag',
  UNTAG = 'untag',
  FORWARD = 'forward',
  MOVE = 'move',
  SENDING_ERROR = 'sending_error',
  MESSAGE_BOUNCE_ERROR = 'message_bounce_error',
  REMINDER = 'reminder',
  TRACKER_REMINDER = 'tracker_reminder',
  TRACKER_UNARCHIVE = 'tracker_unarchive',
  TRACKER_SHARE = 'tracker_share',
  TRACKER_UNSHARE = 'tracker_unshare',
  RULE_NOTIFY = 'rule_notify',
  SPLIT_ORIGIN = 'conv_orig_split',
  SPLIT_TARGET = 'conv_dest_split',
  MERGE = 'conv_join',
}

// Other.
export enum OtherActivityTypesEnum {
  MESSAGE_SEEN = 'message_seen',
  OTHER = 'other',
}

const supportedTypeList = [
  ...values(CommentActivityTypesEnum),
  ...values(MessageActivityTypesEnum),
  ...values(BasicActivityTypesEnum),
];
const supportedTypes = new Set(supportedTypeList);

export enum BasicVisualTypesEnum {
  ASSIGNEE = 'ASSIGNEE',
  ERROR = 'ERROR',
  INBOX = 'INBOX',
  TAG = 'TAG',
  TOPIC = 'TOPIC',
  SPLIT_ORIGIN = 'SPLIT_ORIGIN',
  SPLIT_TARGET = 'SPLIT_TARGET',
  REMINDER = 'REMINDER',
  RULE_FORWARD = 'RULE_FORWARD',
  TRACKER_SHARE = 'TRACKER_SHARE',
  TRACKER_UNSHARE = 'TRACKER_UNSHARE',
  PLAIN = 'PLAIN',
}

export type ActivityTypesEnum =
  | MessageActivityTypesEnum
  | CommentActivityTypesEnum
  | BasicActivityTypesEnum
  | OtherActivityTypesEnum;

export const ActivityTypesEnumValue = {
  ...MessageActivityTypesEnum,
  ...CommentActivityTypesEnum,
  ...BasicActivityTypesEnum,
  ...OtherActivityTypesEnum,
};

export enum ActivityTopicTypesEnum {
  ASANA = 'asana',
  GITHUB = 'github',
  JIRA = 'jira',
  TRELLO = 'trello',
  LINEAR = 'linear',
  MONDAY = 'monday',
  WEB = 'web',
}

export const RActivityType = makeEnumRuntimeType(ActivityTypesEnumValue);

export enum ActivityVisualTypesEnum {
  MESSAGE = 'MESSAGE',
  COMMENT = 'COMMENT',
  BASIC = 'BASIC',
  OTHER = 'OTHER',
}

/*
 * Runtime types.
 */

const RBaseActivity = t.type({
  id: t.Integer,
  url: t.string,
  conversationId: t.Integer,
  type: RActivityType,
  date: t.Integer,
  inboxIds: t.union([t.undefined, t.readonlyArray(RInboxIdRef)]),
  pinnedByTeammateId: t.union([RLazyTeammateIdRef, t.undefined]),
  pinnedAt: t.union([t.undefined, t.Integer]),
  updatedAt: t.Integer,
  deletedAt: t.union([t.undefined, t.Integer]),
});

const RMessageActivity = t.intersection([
  RBaseActivity,
  t.type({
    visualType: t.literal(ActivityVisualTypesEnum.MESSAGE),
    source: t.union([RMessageActivitySource, t.undefined]),
    messageUid: RMessageUidRef,
    messageId: t.union([t.undefined, t.Integer]),
    authorId: t.union([RLazyTeammateIdRef, t.undefined]),
  }),
]);

export const RCommentActivity = t.intersection([
  RBaseActivity,
  t.type({
    visualType: t.literal(ActivityVisualTypesEnum.COMMENT),
    commentUid: RCommentUidRef,
    mentionedAliases: t.readonlyArray(t.string),
    authorId: t.union([RLazyTeammateIdRef, t.undefined]),
    isTombstoned: t.union([t.undefined, t.boolean]),
  }),
]);

const RBaseBasicActivity = t.intersection([
  RBaseActivity,
  t.type({
    visualType: t.literal(ActivityVisualTypesEnum.BASIC),
    source: RActivitySource,
  }),
]);

const RBasicActivityError = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.ERROR),
    error: t.union([t.string, t.undefined]),
  }),
]);

const RBasicActivityAssignee = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.ASSIGNEE),
    assigneeId: RLazyTeammateIdRef,
  }),
]);

const RBasicActivityInbox = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.INBOX),
    inboxId: RInboxIdRef,
  }),
]);

const RBasicActivityTag = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.TAG),
    tagId: RTagIdRef,
  }),
]);

const RBasicActivityTopic = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.TOPIC),
    topicId: t.union([RTopicIdRef, t.undefined]),
  }),
]);

const RBasicActivitySplitOrigin = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.SPLIT_ORIGIN),
    targetConversationId: t.number,
  }),
]);

const RBasicActivitySplitTarget = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.SPLIT_TARGET),
    originConversationId: t.number,
  }),
]);

const RBasicActivityRuleForward = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.RULE_FORWARD),
    contactHandle: t.string,
  }),
]);

const RBasicActivityTrackerShare = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.TRACKER_SHARE),
    recipientId: t.union([RLazyTeammateIdRef, t.undefined]),
  }),
]);

const RBasicActivityTrackerUnshare = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.TRACKER_UNSHARE),
    recipientId: t.union([RLazyTeammateIdRef, t.undefined]),
  }),
]);

const RBasicActivityReminder = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.REMINDER),
    reminderId: t.number,
  }),
]);

const RBasicActivityPlain = t.intersection([
  RBaseBasicActivity,
  t.type({
    basicVisualType: t.literal(BasicVisualTypesEnum.PLAIN),
  }),
]);

const RBasicActivity = t.intersection([
  t.type({
    visualType: t.literal(ActivityVisualTypesEnum.BASIC),
  }),
  t.taggedUnion('basicVisualType', [
    RBasicActivityError,
    RBasicActivityAssignee,
    RBasicActivityInbox,
    RBasicActivityTag,
    RBasicActivityTopic,
    RBasicActivityPlain,
    RBasicActivitySplitOrigin,
    RBasicActivitySplitTarget,
    RBasicActivityRuleForward,
    RBasicActivityReminder,
    RBasicActivityTrackerShare,
    RBasicActivityTrackerUnshare,
  ]),
]);

const ROtherActivity = t.intersection([
  RBaseActivity,
  t.type({
    visualType: t.literal(ActivityVisualTypesEnum.OTHER),
    source: RActivitySource,
  }),
]);

const RActivityDirty = t.readonly(
  t.taggedUnion('visualType', [RMessageActivity, RCommentActivity, RBasicActivity, ROtherActivity]),
  'activity',
);

const RORMessageActivity = t.readonly(RMessageActivity);
const RORCommentActivity = t.readonly(RCommentActivity);
const RORBasicActivityError = t.readonly(RBasicActivityError);
const RORBasicActivityAssignee = t.readonly(RBasicActivityAssignee);
const RORBasicActivityInbox = t.readonly(RBasicActivityInbox);
const RORBasicActivityTag = t.readonly(RBasicActivityTag);
const RORBasicActivityTopic = t.readonly(RBasicActivityTopic);
const RORBasicActivitySplitOrigin = t.readonly(RBasicActivitySplitOrigin);
const RORBasicActivitySplitTarget = t.readonly(RBasicActivitySplitTarget);
const RORBasicActivityRuleForward = t.readonly(RBasicActivityRuleForward);
const RORBasicActivityTrackerShare = t.readonly(RBasicActivityTrackerShare);
const RORBasicActivityTrackerUnshare = t.readonly(RBasicActivityTrackerUnshare);
const RORBasicActivityPlain = t.readonly(RBasicActivityPlain);
const RORBasicActivityReminder = t.readonly(RBasicActivityReminder);
const ROROtherActivity = t.readonly(ROtherActivity);

/*
 * Types.
 */

export interface MessageActivityModel extends t.TypeOf<typeof RORMessageActivity> {}
export interface CommentActivityModel extends t.TypeOf<typeof RORCommentActivity> {}
export interface BasicActivityErrorModel extends t.TypeOf<typeof RORBasicActivityError> {}
export interface BasicActivityAssigneeModel extends t.TypeOf<typeof RORBasicActivityAssignee> {}
export interface BasicActivityInboxModel extends t.TypeOf<typeof RORBasicActivityInbox> {}
export interface BasicActivityTagModel extends t.TypeOf<typeof RORBasicActivityTag> {}
export interface BasicActivityTopicModel extends t.TypeOf<typeof RORBasicActivityTopic> {}
export interface BasicActivitySplitOriginModel extends t.TypeOf<typeof RORBasicActivitySplitOrigin> {}
export interface BasicActivitySplitTargetModel extends t.TypeOf<typeof RORBasicActivitySplitTarget> {}
export interface BasicActivityReminderModel extends t.TypeOf<typeof RORBasicActivityReminder> {}
export interface BasicActivityRuleForwardModel extends t.TypeOf<typeof RORBasicActivityRuleForward> {}
export interface BasicActivityTrackerShareModel extends t.TypeOf<typeof RORBasicActivityTrackerShare> {}
export interface BasicActivityTrackerUnshareModel extends t.TypeOf<typeof RORBasicActivityTrackerUnshare> {}
export interface BasicActivityPlainModel extends t.TypeOf<typeof RORBasicActivityPlain> {}
export interface OtherActivityModel extends t.TypeOf<typeof ROROtherActivity> {}

export type BasicActivityModel =
  | BasicActivityErrorModel
  | BasicActivityAssigneeModel
  | BasicActivityInboxModel
  | BasicActivityTagModel
  | BasicActivityTopicModel
  | BasicActivitySplitOriginModel
  | BasicActivitySplitTargetModel
  | BasicActivityReminderModel
  | BasicActivityRuleForwardModel
  | BasicActivityTrackerShareModel
  | BasicActivityTrackerUnshareModel
  | BasicActivityPlainModel;

export type ActivityModel =
  | MessageActivityModel
  | CommentActivityModel
  | BasicActivityModel
  | OtherActivityModel;

export const RActivity = t.clean<ActivityModel>(RActivityDirty as any);
export const RActivityIdRef = makeIdRefType(RActivity);
const RActivities = t.readonlyArray(RActivity);

/*
 * IO methods.
 */

export function parseActivity(src: any): ActivityModel {
  // Boundary validation of the model consistency.
  const type = parseActivityType(src.type);
  const visualType = computeActivityVisualTypeFromType(type);
  const messageUid = (src.message && src.message.uid) || src.message_uid;
  const messageId = (src.message && src.message.id) || undefined;
  const commentUid = (src.comment && src.comment.uid) || src.comment_uid;
  const isTombstoned = src.is_tombstoned;
  const commentMentions = (src.comment && Array.isArray(src.comment.mentions) && src.comment.mentions) || [];
  const authorId = (src.from && src.from.id) || undefined;
  const inboxIds = src.inbox_ids || undefined;
  const deletedAt = src.deleted_at || undefined;
  const updatedAt = src.updated_at || src.date;

  if (!src.updated_at) {
    activityModelLogger.warn(
      'No `updated_at` property found when parsing Activity. Using `date` as fallback',
      {activity_blob: src},
    );
  }

  switch (visualType) {
    case ActivityVisualTypesEnum.MESSAGE:
      return {
        id: src.id,
        url: parseActivityUrl(src),
        conversationId: src.conversation_id,
        source: parseMessageActivitySource(src),
        type,
        visualType,
        messageUid,
        messageId,
        authorId,
        inboxIds,
        date: src.date,
        pinnedByTeammateId: src.pinned_by_teammate_id,
        pinnedAt: src.pinned_at,
        updatedAt,
        deletedAt,
      };

    case ActivityVisualTypesEnum.COMMENT:
      return {
        id: src.id,
        url: parseActivityUrl(src),
        conversationId: src.conversation_id,
        type,
        visualType,
        commentUid,
        authorId,
        inboxIds,
        mentionedAliases: commentMentions,
        date: src.date,
        pinnedByTeammateId: src.pinned_by_teammate_id,
        pinnedAt: src.pinned_at,
        updatedAt,
        deletedAt,
        isTombstoned,
      };

    case ActivityVisualTypesEnum.OTHER:
      return {
        id: src.id,
        url: parseActivityUrl(src),
        conversationId: src.conversation_id,
        type,
        visualType,
        date: src.date,
        inboxIds,
        pinnedByTeammateId: src.pinned_by_teammate_id,
        pinnedAt: src.pinned_at,
        updatedAt,
        source: parseActivitySource(src),
        deletedAt,
      };

    default:
      return parseBasicActivity(src, type);
  }
}

export function parseActivityType(type: any) {
  switch (type) {
    case 'tag_private':
      return BasicActivityTypesEnum.TAG;

    case 'untag_private':
      return BasicActivityTypesEnum.UNTAG;

    case 'conv_orig_auto_split':
      return BasicActivityTypesEnum.SPLIT_ORIGIN;

    case 'conv_dest_auto_split':
      return BasicActivityTypesEnum.SPLIT_TARGET;

    default:
      return supportedTypes.has(type) ? type : OtherActivityTypesEnum.OTHER;
  }
}

export function computeActivityVisualTypeFromType(type: ActivityTypesEnum): ActivityVisualTypesEnum {
  switch (type) {
    case MessageActivityTypesEnum.INBOUND:
    case MessageActivityTypesEnum.OUTBOUND:
    case MessageActivityTypesEnum.OUT_REPLY:
      return ActivityVisualTypesEnum.MESSAGE;

    case CommentActivityTypesEnum.COMMENT:
    case CommentActivityTypesEnum.MENTION:
      return ActivityVisualTypesEnum.COMMENT;

    case OtherActivityTypesEnum.OTHER:
      return ActivityVisualTypesEnum.OTHER;

    default:
      return ActivityVisualTypesEnum.BASIC;
  }
}

function parseBasicActivity(src: any, type: any): BasicActivityModel {
  const basicVisualType = computeBasicActivityVisualType(src);
  const commonProps = {
    id: src.id,
    url: parseActivityUrl(src),
    conversationId: src.conversation_id,
    type,
    date: src.date,
    inboxIds: src.inbox_ids || undefined,
    pinnedByTeammateId: src.pinned_by_teammate_id,
    pinnedAt: src.pinned_at,
    updatedAt: src.updated_at || src.date,
    source: parseActivitySource(src),
    deletedAt: src.deleted_at || undefined,
  };

  switch (basicVisualType) {
    case BasicVisualTypesEnum.ERROR:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        error: (isObject(src.error) && (src.error.reason || src.error.code)) || undefined,
      };

    case BasicVisualTypesEnum.ASSIGNEE:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        assigneeId: src.assignee.id,
      };

    case BasicVisualTypesEnum.INBOX:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        inboxId: src.inbox_id || (src.inbox && src.inbox.id),
      };

    case BasicVisualTypesEnum.TAG:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        tagId: src.tag.id,
      };

    case BasicVisualTypesEnum.TOPIC:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        topicId: src.topic_id || undefined,
      };

    case BasicVisualTypesEnum.SPLIT_ORIGIN:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        targetConversationId: src.conversation_dest_id,
      };

    case BasicVisualTypesEnum.SPLIT_TARGET:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        originConversationId: src.conversation_orig_id,
      };

    case BasicVisualTypesEnum.REMINDER:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        reminderId: src.reminder_id || undefined,
      };

    case BasicVisualTypesEnum.RULE_FORWARD:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        contactHandle: src.contact.handle,
      };

    case BasicVisualTypesEnum.TRACKER_SHARE:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        recipientId: (src.recipients && src.recipients[0] && src.recipients[0].id) || undefined,
      };

    case BasicVisualTypesEnum.TRACKER_UNSHARE:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
        recipientId: (src.recipients && src.recipients[0] && src.recipients[0].id) || undefined,
      };

    case BasicVisualTypesEnum.PLAIN:
      return {
        ...commonProps,
        visualType: ActivityVisualTypesEnum.BASIC,
        basicVisualType,
      };

    default:
      throw new Error('This should never happen.');
  }
}

export function computeBasicActivityVisualType(src: any): BasicVisualTypesEnum {
  if (
    src.error ||
    src.type === BasicActivityTypesEnum.SENDING_ERROR ||
    src.type === BasicActivityTypesEnum.MESSAGE_BOUNCE_ERROR
  ) {
    return BasicVisualTypesEnum.ERROR;
  }

  if (src.assignee && src.assignee.id) {
    return BasicVisualTypesEnum.ASSIGNEE;
  }

  if (src.inbox_id || (src.inbox && src.inbox.id)) {
    return BasicVisualTypesEnum.INBOX;
  }

  if (src.tag && src.tag.id) {
    return BasicVisualTypesEnum.TAG;
  }

  if ((src.topic_type || src.topic_type_raw) && src.topic_name) {
    return BasicVisualTypesEnum.TOPIC;
  }

  if (src.conversation_dest_id) {
    return BasicVisualTypesEnum.SPLIT_ORIGIN;
  }

  if (src.conversation_orig_id) {
    return BasicVisualTypesEnum.SPLIT_TARGET;
  }

  if (src.reminder_id) {
    return BasicVisualTypesEnum.REMINDER;
  }

  if (src.type === BasicActivityTypesEnum.FORWARD && src.rule_id) {
    return BasicVisualTypesEnum.RULE_FORWARD;
  }

  if (src.type === BasicActivityTypesEnum.TRACKER_SHARE) {
    return BasicVisualTypesEnum.TRACKER_SHARE;
  }

  if (src.type === BasicActivityTypesEnum.TRACKER_UNSHARE) {
    return BasicVisualTypesEnum.TRACKER_UNSHARE;
  }

  return BasicVisualTypesEnum.PLAIN;
}

// Activities have 2 urls forms that the backend may send our way.
//     1. company/:id/activities/:activityId
//     2. company/:id/conversations/:conversationId/timeline/:activityId
//
// On the Frontend, we actually only care about the latter (i.e. #2).
//
// For historical reasons, `src.url` is usually #2, but will occasionally be #1 depending on the request.
// However `src.in_conversation_url` will ALWAYS be of form #2, so that is preferred.
function parseActivityUrl(src: any): string {
  return src.in_conversation_url || src.url;
}

export function parseActivities(src: any): ReadonlyArray<ActivityModel> {
  return src.activities && src.activities.map(parseActivity);
}

/*
 * API type.
 */

export const ApiActivity = makeApiType('activity', RActivity, parseActivity);
export const ApiActivities = makeApiType('activities', RActivities, parseActivities);

/*
 * Helpers.
 */

export function isMessageActivity(activity: ActivityModel): activity is MessageActivityModel {
  return activity.visualType === ActivityVisualTypesEnum.MESSAGE;
}

export function isBasicActivity(activity: ActivityModel): activity is BasicActivityModel {
  return activity.visualType === ActivityVisualTypesEnum.BASIC;
}

/*
 * Activity Pinning.
 */

export type PinnedActivityModel = ActivityModel & {pinnedByTeammateId: number};

// To save on bandwidth, whether or not an Activity is pinned is inferred from the presence of `pinnedByTeammateId`.
export const isActivityPinned = (activity: ActivityModel): activity is PinnedActivityModel =>
  activity.pinnedByTeammateId !== undefined;
